import { c as _c } from "react/compiler-runtime";
import { CallSolid, Link as LinkIcon, MapPinSolid } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { Image, StructuredText } from 'react-datocms';
import { AccessTimes } from '~/components/AccessTimes/AccessTimes';
import { Link } from '~/components/Link';
import { MessagePanel } from '~/components/MessagePanel';
import { ThumbsUpAndDownVote } from '~/components/ThumbsUpAndDownVote/';
import type { Time } from '~/types/AccommodationBooking.types';
import { expandedBoardBasis } from '~/utils/accommodationHelpers';
import { hasFeatureText, isEmptyStructuredText, isNotEmptyString } from '~/utils/datoHelpers';
import type { AccommodationProps } from './Accommodation.types';
const Accommodation = t0 => {
  const $ = _c(12);
  const {
    bookings,
    isDetailed,
    isMinimal
  } = t0;
  const t = useTranslations("journey");
  const ownAccommodation = bookings?.[0].accommodation === null;
  const formattedTime = _temp;
  if (ownAccommodation || !bookings) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="mb-24px"><MessagePanel message="You've arranged your own accommodation, so this won't form part of your Byway holiday package." /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== bookings || $[2] !== isDetailed || $[3] !== isMinimal || $[4] !== t) {
    let t2;
    if ($[6] !== isDetailed || $[7] !== isMinimal || $[8] !== t) {
      t2 = booking => {
        const {
          board,
          bookingReference,
          endDate,
          notes,
          room,
          startDate,
          rooms
        } = booking;
        const {
          address,
          checkInTime,
          checkOutTime,
          id,
          images,
          location,
          phoneNumber,
          publicNotes,
          websiteUrl,
          titleOverride,
          title
        } = booking.accommodation || {};
        return <div key={`accommodation-booking-${id}`}><header className="flex align-center mb-20px mt-16px">{images?.[0]?.responsiveImage && <div className="w-[60px] h-[60px] mr-8px relative shrink-0 print:hidden"><Image data={images[0].responsiveImage} layout="fill" objectFit="cover" /></div>}<div><h4 className="capitalize typography-subtitle-2 mb-4px">{hasFeatureText(titleOverride, title)}</h4><p className="typography-body-2">{address}</p></div></header><AccessTimes type="accommodation" startDate={startDate || undefined} endDate={endDate || undefined} startTime={formattedTime(checkInTime || null) || "TBC"} endTime={formattedTime(checkOutTime || null) || "TBC"} />{publicNotes && <><h3 className="typography-chip-style text-neutral-600 mb-8px">Good to know</h3><div className="typography-body-2 pb-16px" dangerouslySetInnerHTML={{
              __html: publicNotes
            }} /></>}{isDetailed && <ul aria-label="Contact details" className="border-t border-b divide-y divide-solid border-neutral-300 divide-neutral-300"><li className="py-16px print:hidden"><Link hasUnderline={false} href={`https://www.google.com/maps/search/?api=1&query=${location?.latitude}%2C${location?.longitude}`} className="flex items-center space-x-2 typography-button" isExternal={true}><span className="flex items-center gap-2 typography-button"><MapPinSolid decorative={true} size="large" /><span>See location</span></span></Link></li>{isNotEmptyString(phoneNumber) && <li className="font-bold py-16px"><Link hasUnderline={false} href={`tel:${phoneNumber}`}><span className="flex items-center gap-2 typography-button flex-nowrap"><CallSolid decorative={true} size="large" /><span>Call {phoneNumber}</span></span></Link></li>}{isNotEmptyString(websiteUrl) && <li className="font-bold py-16px"><Link hasUnderline={false} href={websiteUrl} isExternal={true}><span className="flex items-center gap-2 typography-button"><LinkIcon decorative={true} strokeWidth={2} size="large" /><span>Website</span></span></Link></li>}</ul>}{(isMinimal || isDetailed) && <dl aria-label="Booking details" className="divide-y divide-solid divide-neutral-300">{isNotEmptyString(bookingReference) && <div className="py-16px"><dt className="typography-chip-style text-neutral-600 mb-8px">Confirmation number</dt><dd className="typography-emphasis-1">{bookingReference}</dd></div>}{rooms && rooms.length > 0 ? <div className="py-4px"><dt className="typography-chip-style text-neutral-600 mt-8px">Room details</dt><div className="divide-y divide-solid divide-neutral-300">{rooms.map((room_0, i) => <div className="py-2" key={i}>{isNotEmptyString(room_0.roomName) && <dd className="typography-body-1">Room: <span className="typography-emphasis-1">{room_0.roomName}</span></dd>}{isNotEmptyString(room_0.board) && <dd className="typography-body-1">{t("board_label")}{" "}<span className="typography-emphasis-1">{expandedBoardBasis(t, room_0.board)}</span></dd>}</div>)}</div></div> : (isNotEmptyString(room) || isNotEmptyString(board)) && <div className="py-16px"><dt className="typography-chip-style text-neutral-600 mb-8px">Room details</dt>{isNotEmptyString(room) && <dd className="typography-body-1">Room: <span className="typography-emphasis-1">{room}</span></dd>}{isNotEmptyString(board) && <dd className="typography-body-1">{t("board_label")}{" "}<span className="typography-emphasis-1">{expandedBoardBasis(t, board)}</span></dd>}</div>}{notes && !isEmptyStructuredText(notes) && <div className="py-16px"><dt className="typography-chip-style text-neutral-600 mb-8px">Reservation notes</dt><dd className="typography-body-1"><StructuredText data={notes} /></dd></div>}{isDetailed && <div className="py-16px"><dt className="typography-chip-style text-neutral-600 mb-8px">Bag drop-off available</dt><dd className="typography-body-1">Yes</dd></div>}</dl>}{isDetailed && <div className="py-4 border-t border-neutral-300">{booking.accommodation?.id && <ThumbsUpAndDownVote itemType="ACCOMMODATION" cmsItemId={booking.accommodation.id} />}</div>}</div>;
      };
      $[6] = isDetailed;
      $[7] = isMinimal;
      $[8] = t;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = bookings.map(t2);
    $[1] = bookings;
    $[2] = isDetailed;
    $[3] = isMinimal;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[10] !== t1) {
    t2 = <div className="mb-4px">{t1}</div>;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
export { Accommodation };
function _temp(time) {
  if (!time?.[0] || time[0].hour === null) {
    return null;
  }
  const {
    hour,
    minute
  } = time[0];
  if (hour && minute === null) {
    time[0].minute = 0;
  }
  return `${hour}:${(minute ?? 0).toString().padStart(2, "0")}`;
}